import { useEffect, useState } from 'react';
import { getStorageQuotesInbox, getStorageQuotesMine } from '@/shared/storage-api';
import { listStorageLocations } from '@/shared/storage-locations-api';
import { subscribeNavCounts } from '@/shared/nav-counts-signal';

/**
 * Sidebar nav badge counts (NEX-1164 chrome reskin, fix #1) — Solicitudes/Mis cotizaciones/Mis
 * depósitos each show the live length of the same list their own page already fetches for its own
 * content (`getStorageQuotesInbox`/`getStorageQuotesMine`/`listStorageLocations` — confirmed these
 * are the real function names against `src/shared/storage-api.ts`/`storage-locations-api.ts`, not
 * assumed from the roadmap doc's terse endpoint names). "Servicios y condiciones" and "Mi empresa"
 * have no entry here: the design shows no badge on either, and there is no "count" for a profile
 * page to show one of anyway.
 *
 * DELIBERATELY NOT a shared cache/context with each page's own fetch of the exact same endpoint:
 * that is a real, independently-useful follow-up (one request serving both "the badge" and "the
 * page"), but it is a bigger data-layer change than this fix calls for — this hook's only job is
 * reading each list's `.length` for the sidebar, called once at `AppLayout.tsx`, not per page. A
 * page that mounts right after still issues its own fetch of the same list; see this folder's
 * `AGENTS.md` for this noted as a follow-up, not solved here.
 *
 * Audit STO-9 (2026-10-30): not once per mount any more — a page that changed one of the lists
 * calls `notifyNavCountsChanged()` (`nav-counts-signal.ts`) and this hook re-reads all three. Still
 * no cache: the page keeps its own fetch, the hook keeps its own.
 *
 * `enabled` (not just an unconditional effect) exists because this hook is called unconditionally
 * at the top of `AppLayout.tsx`, above its own `loading`/`isAuthenticated` branches — React's Rules
 * of Hooks forbid calling it only after those checks. Without this flag every one of the three
 * calls would also fire while the session is still loading or not authenticated at all, each
 * failing (401/403, no valid token yet) before ever reaching `/login` — wasted requests, and in a
 * test, three real `fetch` calls `AppLayout.test.tsx`'s pre-auth-resolution cases never asked for.
 * Pass `isAuthenticated && !loading` from the one real caller.
 */
export interface NavCounts {
  [navItemId: string]: number | undefined;
}

/**
 * One list failing (`Promise.allSettled`, not `Promise.all`) must never blank the other two badges
 * — a `/storage/locations` outage has nothing to do with whether Solicitudes has a real count to
 * show. A still-loading or failed entry stays `undefined`, which the package `AppShell` renders as no
 * badge at all — never a fabricated `0` — same "degrade, don't crash or lie" convention every other fetch
 * in this app already follows (e.g. `DepositosPage.tsx`'s own load effect).
 */
export function useNavCounts(enabled: boolean): NavCounts {
  const [counts, setCounts] = useState<NavCounts>({});

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    // A notification while a read is still in flight starts another; only the latest one may
    // write, so a slow earlier answer never overwrites a newer count.
    let latest = 0;

    async function load() {
      const run = ++latest;
      const [inbox, mine, locations] = await Promise.allSettled([
        getStorageQuotesInbox(),
        getStorageQuotesMine(),
        listStorageLocations()
      ]);
      if (cancelled || run !== latest) return;

      setCounts({
        solicitudes: inbox.status === 'fulfilled' ? inbox.value.length : undefined,
        cotizaciones: mine.status === 'fulfilled' ? mine.value.length : undefined,
        depositos: locations.status === 'fulfilled' ? locations.value.length : undefined
      });
    }

    load();
    const unsubscribe = subscribeNavCounts(load);
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [enabled]);

  return counts;
}
